<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import countries from 'shared/constants/countries.js';

const props = defineProps({
  modelValue: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const open = ref(false);
const search = ref('');
const rootRef = ref(null);
const searchRef = ref(null);

const selected = computed(
  () => countries.find(c => c.id === props.modelValue) || null
);

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return countries;
  return countries.filter(
    c =>
      c.name.toLowerCase().includes(q) ||
      c.dial_code.toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q)
  );
});

function onDocPointer(event) {
  if (rootRef.value && !rootRef.value.contains(event.target)) close();
}

function close() {
  if (!open.value) return;
  open.value = false;
  document.removeEventListener('mousedown', onDocPointer);
}

function toggle() {
  if (open.value) {
    close();
    return;
  }
  open.value = true;
  search.value = '';
  document.addEventListener('mousedown', onDocPointer);
  nextTick(() => searchRef.value?.focus());
}

function select(country) {
  emit('update:modelValue', country.id);
  close();
}

onBeforeUnmount(() => document.removeEventListener('mousedown', onDocPointer));
</script>

<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      class="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
      @click="toggle"
    >
      <span class="flex items-center gap-2 min-w-0">
        <span v-if="selected" class="text-base leading-none">{{
          selected.emoji
        }}</span>
        <span class="truncate">
          {{ selected ? `${selected.name} (${selected.dial_code})` : '' }}
        </span>
      </span>
      <svg
        class="w-3.5 h-3.5 text-n-slate-10 shrink-0"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        viewBox="0 0 24 24"
      >
        <path d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div
      v-if="open"
      class="absolute z-50 mt-1 w-full min-w-[16rem] rounded-lg border border-n-weak bg-n-background shadow-lg"
    >
      <div class="p-2 border-b border-n-weak">
        <input
          ref="searchRef"
          v-model="search"
          type="text"
          :placeholder="t('FORM_BUILDER.FIELD.COUNTRY_SEARCH')"
          class="w-full px-2.5 py-1.5 rounded-md bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12 placeholder-n-slate-9"
        />
      </div>
      <ul class="max-h-56 overflow-y-auto py-1">
        <li
          v-for="country in filtered"
          :key="country.id"
          class="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-n-alpha-2"
          :class="
            country.id === modelValue
              ? 'bg-n-blue-2/50 text-n-brand'
              : 'text-n-slate-12'
          "
          @click="select(country)"
        >
          <span class="text-base leading-none">{{ country.emoji }}</span>
          <span class="truncate flex-1">{{ country.name }}</span>
          <span class="text-xs text-n-slate-10">{{ country.dial_code }}</span>
        </li>
        <li
          v-if="filtered.length === 0"
          class="px-3 py-3 text-sm text-center text-n-slate-9"
        >
          {{ t('FORM_BUILDER.FIELD.COUNTRY_EMPTY') }}
        </li>
      </ul>
    </div>
  </div>
</template>
